import React, { useMemo } from "react";
import { TextInput, type TextInputProps } from "react-native";

import { ErrorText, Flex, Label } from "../../../../../../../../../../mads";
import { Text } from "../../../../../../../../../../mads/Text";
import { MinimumPay as CoreMinimumPay } from "../../../../../../../../../../shared/components/MinimumPay";
import {
  type GettextInterface,
  useI18n,
  useLocale,
} from "../../../../../../../../../../shared/I18n";
import { getLocaleCurrency } from "../../../../../../../../../../shared/util/salary/getLocaleCurrency";
import { parseLocaleCurrencyAmount } from "../../../../../../../../../../shared/util/salary/parseLocaleCurrencyAmount";
import type Sentiment from "../../../../../../../../../../shared/util/salary/Sentiment";
import { useViewjobProctorAllocation } from "../../../../../../../../../proctor/__generated__/ViewjobProctorContext";
import { JmRnReusablePayComponentTst } from "../../../../../../../../../proctor/__generated__/ViewjobProctorSpecifications";
import { EmployersPayInfoAlert } from "./EmployersPayInfoAlert";
import { PayPeriodSelect } from "./PayPeriodSelect";
import { styles } from "./styles";
import { validatePayRange } from "./validation";

export interface SalaryPreference {
  amount?: string;
  salaryType?: string;
  sentiment?: Sentiment;
}

interface MinimumPayProps {
  value?: SalaryPreference;
  onChange: (salaryPreference: SalaryPreference) => void;
  showErrors?: boolean;
}

// Helper function to get error messages (for local implementation)
export function getPayErrorMessages({
  gt,
  value,
  showErrors,
  locale,
}: {
  value?: SalaryPreference;
  gt: GettextInterface;
  showErrors?: boolean;
  locale: string;
}) {
  const isAmountZero = !!value?.amount && parseLocaleCurrencyAmount(value?.amount, locale) === 0;
  const payRangeValidationError = (value && validatePayRange(value, locale, gt)) || undefined;

  return {
    fieldsetError: payRangeValidationError,
    inputError:
      showErrors && (!value?.amount || isAmountZero) && !payRangeValidationError
        ? isAmountZero
          ? gt.pgettext(
              "Minimum pay greater than zero error text",
              "Enter an amount greater than 0",
            )
          : gt.pgettext("Minimum pay required error text", "Minimum pay is required")
        : "",
    selectError:
      showErrors && !value?.salaryType && !payRangeValidationError
        ? gt.pgettext("Pay period error text", "Pay period is required")
        : "",
  };
}

// PayInput component for the local implementation
const PayInput = ({ invalid, ...inputProps }: TextInputProps & { invalid: boolean }) => {
  const { country, language } = useLocale();
  const locale = `${language ?? "en"}-${country ?? "US"}`;
  const { hasRighthandSymbol, currencySymbol } = getLocaleCurrency(locale);
  const currencyElement = <Text sx={styles.payInputCurrency}>{currencySymbol}</Text>;
  const wrapperStyles = {
    ...styles.payInputWrapper,
    ...(invalid ? styles.payInputWrapperInvalid : {}),
  };

  return (
    <Flex sx={wrapperStyles}>
      {!hasRighthandSymbol && currencyElement}
      <TextInput style={styles.payInput} inputMode="decimal" {...inputProps} />
      {hasRighthandSymbol && currencyElement}
    </Flex>
  );
};

// Local implementation - ViewJob-specific MinimumPayForm with inline logic
const LocalMinimumPayForm: React.FC<MinimumPayProps> = ({ value, onChange, showErrors }) => {
  const { country, language } = useLocale();
  const locale = `${language ?? "en"}-${country ?? "US"}`;
  const gt = useI18n();
  const basePayLabel = gt.gettext("Minimum base pay");

  const { fieldsetError, inputError, selectError } = getPayErrorMessages({
    gt,
    value,
    showErrors,
    locale,
  });

  const formattedValue = useMemo(() => {
    if (!value?.amount) {
      return "";
    }
    const numberFormat = new Intl.NumberFormat(locale);
    const amount = parseLocaleCurrencyAmount(value.amount, locale);
    return isNaN(amount) ? value.amount : numberFormat.format(amount);
  }, [value?.amount, locale]);

  return (
    <Flex sx={{ gap: 16 }}>
      <EmployersPayInfoAlert />
      <Flex sx={styles.updatedWrapper}>
        <Flex sx={{ marginBottom: 4 }}>
          <Label text={basePayLabel} isRequired={true} />
          <PayInput
            value={formattedValue}
            onChangeText={(text) => {
              const amount = text.replace(/[^\d,.]/gi, "");
              onChange({
                amount: amount,
                salaryType: value?.salaryType,
                sentiment: value?.sentiment,
              });
            }}
            aria-required
            aria-label={basePayLabel}
            invalid={!!inputError || !!fieldsetError}
          />
          {inputError && <ErrorText>{inputError}</ErrorText>}
        </Flex>
        <PayPeriodSelect
          invalid={!!selectError || !!fieldsetError}
          value={value}
          onChange={onChange}
          errorText={selectError}
          showRequiredIndicator={true}
        />
        {fieldsetError && <ErrorText>{fieldsetError}</ErrorText>}
      </Flex>
    </Flex>
  );
};

// Reusable implementation - Uses the shared core MinimumPay component
const ReusableMinimumPayForm: React.FC<MinimumPayProps> = ({ value, onChange, showErrors }) => {
  return (
    <CoreMinimumPay
      value={value}
      onChange={onChange}
      showErrors={showErrors}
      showEmployerAlert={true}
      variant="default"
    />
  );
};

// Main export that conditionally uses local or reusable implementation based on proctor
export const MinimumPayForm: React.FC<MinimumPayProps> = ({ value, onChange, showErrors }) => {
  // Check if the reusable pay component test is active (value 1 = test bucket)
  // value -1 = inactive, value 0 = control, value 1 = test
  const isJmRnReusablePayComponentTstActive =
    useViewjobProctorAllocation(JmRnReusablePayComponentTst).value === 1;

  // Use reusable core component when proctor is in test bucket, otherwise use local implementation
  if (isJmRnReusablePayComponentTstActive) {
    return <ReusableMinimumPayForm value={value} onChange={onChange} showErrors={showErrors} />;
  }

  return <LocalMinimumPayForm value={value} onChange={onChange} showErrors={showErrors} />;
};
